import moment from "moment";
import {
  getSubscriptionsRaw,
  getCustomerByEmail,
  getSubscriptionsByCustomer,
} from "./recnetApi";

/**
 * Interpretación del estado de suscripción de Stripe.
 *
 * El endpoint masivo (`/users/subscriptions`) devuelve la foto actual: no hay
 * histórico. Para "cuántos se suscribieron este mes" ver
 * `services/snapshotDiario.js`.
 */

/** Precio de lista de Recnet Plus (MXN). Ver recnet-24 PaymentMethod/index.js. */
export const PRECIO_LISTA_MXN = 199;

/** Estados de Stripe que cuentan como membresía vigente. */
const ESTADOS_ACTIVOS = new Set(["active", "trialing", "past_due"]);

/** Días de anticipación con los que se avisa de una vigencia por vencer. */
export const DIAS_POR_VENCER = 30;

/** Acepta segundos, milisegundos o ISO y devuelve segundos. */
export function parseToSeconds(val) {
  if (!val) return null;
  if (typeof val === "number") {
    if (!isFinite(val) || val <= 0) return null;
    return val >= 1e12 ? Math.floor(val / 1000) : Math.floor(val);
  }
  if (typeof val === "string") {
    if (/^\d+$/.test(val)) return parseToSeconds(Number(val));
    const m = moment(val, [moment.ISO_8601, "YYYY-MM-DD", "DD/MM/YYYY", "DD-MM-YYYY"], true);
    if (m.isValid()) return Math.floor(m.valueOf() / 1000);
  }
  return null;
}

/**
 * Índice { emailEnMinusculas: { active, current_period_end, status } }.
 * Una sola petición para todo el padrón, en lugar de una por usuario.
 */
export async function cargarIndiceSuscripciones() {
  const arr = await getSubscriptionsRaw();
  const idx = {};
  for (const it of arr) {
    const email = String(it.email || it.customer_email || "").toLowerCase().trim();
    if (!email) continue;
    idx[email] = {
      active: !!it.active,
      current_period_end: parseToSeconds(it.current_period_end || it.currentPeriodEnd),
      status: it.status ? String(it.status).toLowerCase() : it.active ? "active" : null,
      cancel_at_period_end: !!it.cancel_at_period_end,
    };
  }
  return idx;
}

/**
 * Consulta puntual a Stripe para un usuario. Es cara (hasta dos peticiones),
 * así que sólo debe usarse cuando el índice masivo se queda corto.
 */
export async function consultarSuscripcionPuntual({ email, stripeId }) {
  let customerId = stripeId;
  if (!customerId && email) {
    const customer = await getCustomerByEmail(email);
    customerId = customer ? customer.id : null;
  }
  if (!customerId) return null;

  const subs = await getSubscriptionsByCustomer(customerId);
  return elegirMejorSuscripcion(subs);
}

/**
 * Un cliente puede arrastrar varias suscripciones. Nos quedamos con la más
 * relevante: primero por estado, y a igualdad de estado, la que vence después.
 */
export function elegirMejorSuscripcion(subs) {
  if (!subs || !subs.length) return null;
  const puntaje = (s) => {
    const status = String(s.status || "").toLowerCase();
    const fin = parseToSeconds(s.current_period_end) || 0;
    if (status === "active") return 1000000000 + fin;
    if (status === "trialing") return 900000000 + fin;
    if (status === "past_due") return 800000000 + fin;
    if (status === "canceled") return 700000000 + fin;
    return fin;
  };
  let mejor = subs[0];
  for (const s of subs) if (puntaje(s) > puntaje(mejor)) mejor = s;
  return mejor;
}

/**
 * Resuelve el estado de membresía de un usuario a partir de la entrada del
 * índice masivo. Devuelve siempre la misma forma para que las vistas no tengan
 * que interpretar Stripe por su cuenta.
 */
export function resolverEstadoMembresia(entrada) {
  const ahora = moment();

  if (!entrada) {
    return {
      activo: false,
      estado: "sin_membresia",
      etiqueta: "Sin membresía",
      variante: "secondary",
      vigenciaHasta: null,
      vigencia: "—",
      diasRestantes: null,
    };
  }

  const sec = parseToSeconds(entrada.current_period_end);
  const fin = sec ? moment.unix(sec) : null;
  const vigenteHastaElFinal = fin && fin.isSameOrAfter(ahora);

  const activo =
    !!entrada.active ||
    (entrada.status && ESTADOS_ACTIVOS.has(entrada.status)) ||
    // Una suscripción cancelada sigue dando servicio hasta el fin del periodo
    // ya pagado: contarla como inactiva desde el día de la cancelación daba
    // números más bajos que los reales.
    ((entrada.cancel_at_period_end || entrada.status === "canceled") && vigenteHastaElFinal);

  const diasRestantes = fin ? fin.diff(ahora, "days") : null;

  let estado = "sin_membresia";
  let etiqueta = "Sin membresía";
  let variante = "secondary";

  if (activo) {
    if (entrada.cancel_at_period_end) {
      estado = "cancelacion_programada";
      etiqueta = "Cancelación programada";
      variante = "warning";
    } else if (diasRestantes !== null && diasRestantes <= DIAS_POR_VENCER) {
      estado = "por_vencer";
      etiqueta = "Por vencer";
      variante = "warning";
    } else {
      estado = "activo";
      etiqueta = "Activo";
      variante = "success";
    }
  } else if (entrada.status === "canceled") {
    estado = "cancelado";
    etiqueta = "Cancelado";
    variante = "danger";
  }

  return {
    activo,
    estado,
    etiqueta,
    variante,
    vigenciaHasta: fin,
    vigencia: fin ? fin.format("DD/MM/YYYY") : "—",
    diasRestantes,
  };
}
